import { faFileSlash } from "@fortawesome/pro-regular-svg-icons";
import { faCloudUpload } from "@fortawesome/pro-regular-svg-icons/faCloudUpload";
import { faPlayCircle, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Card, CardActions, CardMedia, Grid, Typography, useTheme } from '@mui/material';
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppImageDimension, AppImages } from "app/appImages";
import { MprcClickable, MprcText } from "components";
import { AsyncUploader } from "components/AsyncUploader";
import { CustomVideoSlideRender } from "components/CustomVideoSlideRender";
import { IconButtonCancel } from "components/iconButtons";
import { useMprcTheme } from "components/MprcThemeProvider";
import { AsyncUploadResult, FileGetDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useSnackbar } from "notistack";
import * as React from 'react';
import { useEffect, useRef, useState } from "react";
import Dropzone, { Accept } from 'react-dropzone';
import { useTranslation } from "react-i18next";
import "react-image-crop/dist/ReactCrop.css";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import { MprcCircularProgressWithLabel } from "./MprcCircularProgressWithLabel";
import { MprcJoinComponents } from "./MprcJoinComponents";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IUploadProps {
  acceptedFiles?: Accept;
  enableClear?: boolean;
  selectedFile?: FileGetDto | null;
  uploadMessage?: string[];
  thumbnailUrl?: string | null;
  maxSize?: number;
  mediaPreview?: boolean;
  error?: boolean;
  disabled?: boolean;
  fileNotAcceptText?: string;
  onClear: () => void;
  onFileUploadStarted?: () => void;
  onFileUploadFinished?: () => void;
  onFileUploadProgress?: (progress: number) => void;
  onFileUploadSuccess?: (data: AsyncUploadResult) => void;
  onFileUploadError?: (error: any) => void;
}

/////////////////////////////////////////////////////////////////////////////////////////////////

export const MprcFileSelection: React.FC<IUploadProps> = (props) => {
  const { t } = useTranslation("shared");
  const { enqueueSnackbar } = useSnackbar();
  const { theme: mprcTheme } = useMprcTheme();
  const theme = useTheme();
  const {
    mediaPreview = false,
    thumbnailUrl,
    selectedFile,
    uploadMessage,
    maxSize,
    error = false,
    disabled = false,
    acceptedFiles = { "*/*": [] },
    fileNotAcceptText,
    enableClear = true
  } = props;

  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [lastUpload, setLastUpload] = useState<AsyncUploadResult | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [dialog, setDialog] = useState<any>(null);

  //
  const asyncUploader = useRef<AsyncUploader>(new AsyncUploader({}));

  // Atualiza eventos do Uploader quando as propriedades do componente mudarem.
  useEffect(() => {
    asyncUploader.current.setEvents(
      {
        onFileUploadSuccess: (data: AsyncUploadResult) => {
          const oldUploadCompleted = lastUpload;

          setLastUpload(data);

          if (props.onFileUploadSuccess)
            props.onFileUploadSuccess(data);

          if (oldUploadCompleted) {
            var files = oldUploadCompleted.mainFile ? [oldUploadCompleted.mainFile.fileId] : [];
            asyncUploader.current.delete(files);
          }
        },
        onFileStarted: () => { setUploading(true); props.onFileUploadStarted && props.onFileUploadStarted(); setProgress(0); },
        onFileFinished: () => { setUploading(false); props.onFileUploadFinished && props.onFileUploadFinished(); },
        onFileUploadProgress: (progress: number) => { props.onFileUploadProgress && props.onFileUploadProgress(progress); setProgress(progress); },
        onFileUploadError: (instance, error) => {

          if (error.message == "canceled") {
            enqueueSnackbar(t("AsyncUpload.UploadCanceled"), { variant: "error" });
          }
          else if (error.storageExceeded == true) {
            enqueueSnackbar(t("AsyncUpload.StorageExceededMessage"), { variant: "error" });
          }
          else if (props.onFileUploadError) props.onFileUploadError(error);
          else enqueueSnackbar(error.message, { variant: "error" });
        }
      }
    );
  }, [props]);

  //
  const handleClear = () => {
    props.onClear();
  };

  ////////////////////////////////////////////////////////////////////////////
  /// EFFECT's
  ////////////////////////////////////////////////////////////////////////////

  // Ao carregar um novo arquivo.
  useEffect(() => {
    if (fileToUpload) {
      asyncUploader.current.uploadSingleFile(fileToUpload);
    }
  }, [fileToUpload]);


  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  ///
  const handleMediaViewer = () => {
    if (selectedFile != null) {
      setDialog(<Lightbox
        open
        close={() => setDialog(null)}
        slides={
          selectedFile.mimetype.startsWith("image")
            ? [{
              type: "image",
              src: selectedFile.url!,
              description: selectedFile.metadata.title,
              height: selectedFile.metadata.height!,
              width: selectedFile.metadata.width!,
            }]
            : selectedFile.mimetype.startsWith("audio") || selectedFile.mimetype.startsWith("video")
              ? [{
                type: "custom-video-slide",
                id: selectedFile.fileId,
                videoId: selectedFile.fileId,
                url: selectedFile.url!,
                description: selectedFile.metadata.title,
                height: selectedFile.metadata.height!,
                width: selectedFile.metadata.width!,
                duration: selectedFile.metadata.duration ?? 0,
                mimetype: selectedFile.mimetype,
                isAudio: selectedFile.mimetype.indexOf("audio") > -1,
                isHls: false,
                autoPlay: true,
                poster: selectedFile.mimetype.indexOf("audio") > -1
                  ? AppImages.AudioPoster
                  : AppImages.VideoMp4Poster,
                tracks: selectedFile.mediaTracks ?? []
              }]
              : []
        }
        render={
          {
            slide: ({ slide, offset }) =>
              "type" in slide && slide.type === "custom-video-slide"
                ? <CustomVideoSlideRender slide={slide} offset={offset} />
                : null,

            buttonPrev: () => null,
            buttonNext: () => null
          }
        }
        zoom={{
          maxZoomPixelRatio: 1,
          zoomInMultiplier: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
          doubleClickMaxStops: 2,
          keyboardMoveDistance: 50,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
          scrollToZoom: true
        }}
        plugins={[Captions, Fullscreen, Zoom]}
      />);
    }
  };

  ///
  const renderSimplePreview = () => {
    if (selectedFile == null)
      return null;

    return (
      <Grid container alignItems="center" spacing={1}>
        <Grid>
          <FontAwesomeIcon icon={faCloudUpload} fixedWidth size="4x" />
        </Grid>
        <Grid>
          <Grid container justifyContent="center" alignItems="center" spacing={1}>
            <Grid>
              <span>{selectedFile.metadata.filename}</span>
              <br />
              <small><span>{Formaters.fileSize(selectedFile.metadata.fileSize)}</span></small>
            </Grid>
            {
              enableClear == true && !disabled &&
              <Grid>
                <IconButtonCancel size="small" onClick={handleClear} />
              </Grid>
            }
          </Grid>
        </Grid>
      </Grid>
    );
  };

  ///
  const renderMediaPreview = () => {
    if (selectedFile == null || selectedFile.url == null)
      return null;

    var url = thumbnailUrl
      , altText = selectedFile.metadata.title ?? selectedFile.metadata.filename
      , videoProviderImg: React.ReactNode = null;

    if (selectedFile.mimetype.startsWith("video")) {
      videoProviderImg = <FontAwesomeIcon icon={faPlayCircle} size="3x" color="white" />;
      if (url == null) url = (selectedFile.mimetype.endsWith("mp4") ? AppImages.VideoMp4Thumbnail : AppImages.VideoProcessingThumbnail);

    }
    else if (selectedFile.mimetype.startsWith("audio")) {
      videoProviderImg = <FontAwesomeIcon icon={faPlayCircle} size="3x" color="white" />;
      if (url == null) url = AppImages.AudioThumbnail;
    }
    else if (selectedFile.mimetype.startsWith("image")) {
      if (url == null) url = selectedFile.url;
    }
    else {
      url = selectedFile.url;
    }

    return (
      <Card sx={{ width: AppImageDimension.ThumbnailWide.width, maxWidth: AppImageDimension.ThumbnailWide.width, ml: 1, flexShrink: 0 }}>
        <div style={{ position: "relative" }}>
          <MprcClickable onClick={handleMediaViewer}>
            <CardMedia
              component="img"
              width={AppImageDimension.ThumbnailWide.width}
              height={AppImageDimension.ThumbnailWide.height}
              image={url}
              alt={altText}
            />
            {
              videoProviderImg &&
              <div className={AppGlobalClassKeys.centerChild}>
                {videoProviderImg}
              </div>
            }
          </MprcClickable>
        </div>
        <CardActions sx={{ justifyContent: "space-between", alignItems: "center" }}>
          {
            !disabled &&
            <div>
              <MprcClickable onClick={handleClear}>
                <MprcText color="error"><FontAwesomeIcon icon={faTrash} fixedWidth /></MprcText>
              </MprcClickable>
            </div>
          }
        </CardActions>
      </Card>
    );
  };

  ///
  const buildNoFile = () => {
    return (
      <Grid container alignItems="center" spacing={1}>
        <Grid>
          <FontAwesomeIcon icon={faFileSlash} fixedWidth size="4x" />
        </Grid>
      </Grid>
    );
  };

  ///
  const buildPreview = () => {
    return selectedFile == null
      ? null
      : mediaPreview && selectedFile?.mimetype.startsWith("image") || selectedFile?.mimetype.startsWith("audio") || selectedFile?.mimetype.startsWith("video")
        ? renderMediaPreview()
        : renderSimplePreview();
  };

  ///
  const buildDropZone = () => {
    return (
      <Dropzone
        multiple={false}
        accept={acceptedFiles}
        maxSize={maxSize}
        onDropRejected={
          (fileRejections) => fileRejections[0]
            .errors
            .forEach(e => e.code == "file-too-large"
              ? enqueueSnackbar(mprcTheme.MprcFileSelection.maxSizeError, { variant: "error" })
              : e.code == "file-invalid-type" && enqueueSnackbar(fileNotAcceptText ?? mprcTheme.MprcFileSelection.fileNotAccept, { variant: "error" })
            )
        }
        onDropAccepted={
          (e) => {
            setFileToUpload(e[0]);
          }
        }
      >
        {
          ({ getRootProps, getInputProps, isDragActive }) => {
            return (
              <Box
                {...getRootProps({
                  style:
                  {
                    borderWidth: 1,
                    padding: theme.spacing(1),
                    borderStyle: "solid",
                    borderColor: isDragActive ? theme.palette.success.main : theme.palette.muted.dark
                  }
                })}
              >
                <input {...getInputProps()} />

                <Grid container justifyContent="center" alignItems="center" spacing={1} width="100%">
                  <Grid>
                    <FontAwesomeIcon icon={faCloudUpload} fixedWidth size="4x" />
                  </Grid>
                  <Grid size="grow" style={{ alignSelf: "center", textAlign: "center" }}>
                    <Typography variant="h6" lineHeight="1.1rem">
                      {
                        uploadMessage
                          ? <MprcJoinComponents components={uploadMessage} separator={<br />} />
                          : <span>{mprcTheme.MprcFileSelection.dropHereText1}</span>
                      }

                      {
                        maxSize &&
                        <Box sx={{ mt: 2 }}>{mprcTheme.MprcFileSelection.maxSize}: <b>{Formaters.fileSize(maxSize)}</b></Box>
                      }
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            );
          }
        }
      </Dropzone>
    );
  };

  ///
  const buildUpload = () => {
    return (
      <Grid container alignItems="center" spacing={1}>
        <Grid>
          <MprcCircularProgressWithLabel value={progress} size="6em" />
        </Grid>
        <Grid >
          {
            fileToUpload != null
              ? (
                <Grid container justifyContent="center" alignItems="center" spacing={1}>
                  <Grid>
                    <span>{fileToUpload.name}</span>
                    <br />
                    <small><span>{Formaters.fileSize(fileToUpload.size)}</span></small>
                  </Grid>
                  <Grid>
                    <IconButtonCancel size="small" onClick={() => asyncUploader.current.cancel()} />
                  </Grid>
                </Grid>
              )
              : null
          }
        </Grid>
      </Grid>
    );
  };

  ///
  return (
    <div
      className={!disabled ? AppGlobalClassKeys.clickable : undefined}
    >
      {dialog}
      <Grid
        container
        wrap="nowrap"
        sx={(theme) => ({ border: error && !disabled ? `solid 1px ${theme.palette.error.main}` : undefined })}
      >
        <Grid width="100%">
          {
            disabled && selectedFile != null
              ? buildPreview()
              : disabled && selectedFile == null
                ? buildNoFile()
                : selectedFile == null && !uploading
                  ? buildDropZone()
                  : selectedFile != null
                    ? buildPreview()
                    : buildUpload()
          }
        </Grid>
      </Grid>
    </div>
  );
};
